import axios from 'axios';
import store from '@/store';

const client = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const resolveToken = () => localStorage.getItem('token') || store.state.auth.token;

client.interceptors.request.use(
  config => {
    const token = resolveToken();
    config.headers = config.headers || {};

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers['Authorization'];
    }
    return config;
  },
  error => Promise.reject(error)
);

client.interceptors.response.use(
  response => {
    const apiResponse = response.data;

    if (apiResponse.success) {
      return apiResponse.data;
    }

    const status = apiResponse.errorCode.status;
    const errorMessage = apiResponse.errorCode.message;

    switch (status) {
      case 400:
        console.log('Invalid request:', errorMessage);
        break;
      case 404:
        console.log('Resource not found:', errorMessage);
        break;
      case 500:
        console.error('Internal Server error:', errorMessage);
        break;
      default:
        console.error('Unexpected error:', errorMessage);
    }

    return Promise.reject(apiResponse);
  },
  error => {
    const status = error.response?.status;

    switch (status) {
      case 401:
        store.dispatch('auth/logout');
        break;
      case 403:
        console.error(
          'Permission denied:',
          error.response?.data?.message || 'Access forbidden'
        );
        break;
      default:
        console.error(
          'Request failed:',
          error.response?.data?.message || error.message
        );
    }

    return Promise.reject(error);
  }
);

// 옵션 객체를 axios config로 변환하는 함수
const buildConfig = (options = {}) => {
  const config = {};
  
  // params 처리 (GET 요청의 쿼리 파라미터)
  if (options.params) {
    config.params = options.params;
  }
  
  // headers 처리 (커스텀 헤더)
  if (options.headers) {
    config.headers = options.headers;
  }
  
  // timeout 처리
  if (options.timeout) {
    config.timeout = options.timeout;
  }
  
  return config;
};

const api = {
  /**
   * GET 요청
   * @param {string} url - 요청 URL
   * @param {Object} options - 옵션 객체
   * @param {Object} options.params - 쿼리 파라미터
   * @param {Object} options.headers - 커스텀 헤더
   * @param {number} options.timeout - 타임아웃 (ms)
   * 
   * 사용 예시:
   * api.get('/users', { params: { page: 1 }, headers: { 'X-Custom': 'value' } })
   * api.get('/users', { params: { page: 1 } }) // 기존 방식도 지원
   */
  get: (url, options = {}) => {
    // 하위 호환성: 두 번째 파라미터가 객체가 아닌 경우 params로 처리
    if (options && typeof options === 'object' && !Array.isArray(options) && !options.params && !options.headers) {
      // 기존 방식: api.get(url, params) 지원
      const hasNonConfigKeys = Object.keys(options).some(key => !['params', 'headers', 'timeout'].includes(key));
      if (hasNonConfigKeys) {
        options = { params: options };
      }
    }
    
    const config = buildConfig(options);
    return client.get(url, config);
  },

  /**
   * POST 요청
   * @param {string} url - 요청 URL
   * @param {Object} data - 요청 데이터
   * @param {Object} options - 옵션 객체
   * @param {Object} options.headers - 커스텀 헤더
   * @param {number} options.timeout - 타임아웃 (ms)
   * 
   * 사용 예시:
   * api.post('/users', data, { headers: { 'X-Page-Url': '/dashboard' } })
   */
  post: (url, data = {}, options = {}) => {
    const config = buildConfig(options);
    return client.post(url, data, config);
  },

  /**
   * PUT 요청
   * @param {string} url - 요청 URL
   * @param {Object} data - 요청 데이터
   * @param {Object} options - 옵션 객체
   * @param {Object} options.headers - 커스텀 헤더
   * @param {number} options.timeout - 타임아웃 (ms)
   */
  put: (url, data = {}, options = {}) => {
    const config = buildConfig(options);
    return client.put(url, data, config);
  },

  /**
   * PATCH 요청
   * @param {string} url - 요청 URL
   * @param {Object} data - 요청 데이터
   * @param {Object} options - 옵션 객체
   * @param {Object} options.headers - 커스텀 헤더
   * @param {number} options.timeout - 타임아웃 (ms)
   */
  patch: (url, data = {}, options = {}) => {
    const config = buildConfig(options);
    return client.patch(url, data, config);
  },

  /**
   * DELETE 요청
   * @param {string} url - 요청 URL
   * @param {Object} options - 옵션 객체
   * @param {Object} options.headers - 커스텀 헤더
   * @param {number} options.timeout - 타임아웃 (ms)
   */
  delete: (url, options = {}) => {
    const config = buildConfig(options);
    return client.delete(url, config);
  },

  /**
   * FormData POST 요청 (파일 업로드용)
   * @param {string} url - 요청 URL
   * @param {FormData} formData - FormData 객체
   * @param {Object} options - 옵션 객체
   * @param {Object} options.headers - 커스텀 헤더 (Content-Type은 자동 설정됨)
   * @param {number} options.timeout - 타임아웃 (ms)
   */
  postFormData: (url, formData, options = {}) => {
    const config = buildConfig(options);
    // FormData의 경우 Content-Type을 multipart/form-data로 설정
    config.headers = {
      'Content-Type': 'multipart/form-data',
      ...config.headers
    };
    return client.post(url, formData, config);
  },
};

export default api;
